import { AdyenCheckout } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import axios from 'axios';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../../Utils/logger';
import PaymentStatus from './PaymentStatus';
import { useBookingTimer } from '../../Context/Timer/useBookingTimer';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import "./payment.css";

import ExpressCheckout from "../payment/ExpressCheckout";
import PaymentAccordion from "../payment/PaymentAccordion";
import PaymentMethodRenderer from "../payment/PaymentMethodRenderer";
import paymentStyles from "../payment/payment.module.css";

import {
  PAYMENT_METHODS,
  PAYMENT_IMAGES,
  buildPaymentMethodRows,
  createPaymentComponent,
  createApplePayComponent,
  createGooglePayComponent,
} from "../payment/paymentConfig";
import { navigateToBookedAppointment } from "../../Utils/navigateToBookedAppointment";
import ErrorBoundary from "../ErrorBoundary";

export { PAYMENT_METHODS, PAYMENT_IMAGES };

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 5 * 60 * 1000;

// UX convenience only — limits accidental retries within a tab session.
// Real enforcement must come from server-side 429 responses per booking UUID.
const generateToken = (key, attempts) => {
  try {
    return JSON.stringify({ key, attempts, timestamp: Date.now() });
  } catch {
    return JSON.stringify({ key, attempts: 0, timestamp: Date.now() });
  }
};

const parseToken = (token) => {
  try {
    const { attempts, timestamp } = JSON.parse(token);
    const isActive = Date.now() - timestamp <= LOCK_DURATION_MS;
    return isActive ? { attempts, expiry: timestamp + LOCK_DURATION_MS } : { attempts: 0, expiry: 0 };
  } catch {
    return { attempts: 0, expiry: 0 };
  }
};

/** Adyen error 14_0422: session exhausted after too many failures or session expired.
 *  The Adyen SDK wraps HTTP errors differently across versions — check every known field. */
function isAdyenSessionExpiredError(err) {
  if (!err || typeof err !== "object") return false;
  const code =
    err.errorCode ??
    err.response?.data?.errorCode ??
    err.error?.errorCode ??
    err.data?.errorCode;
  if (code === "14_0422") return true;
  // 422 from checkoutshopper-*.adyen.com always means session invalid/exhausted
  const status =
    err.status ??
    err.httpStatus ??
    err.response?.status ??
    err.error?.status;
  if (status === 422) return true;
  // SDK message fallback
  const msg = typeof err.message === "string" ? err.message : "";
  return msg.includes("session identifier") || msg.includes("14_0422");
}

const LOCK_KEYS = [...Object.values(PAYMENT_METHODS), "card_new", "card_saved"];

function unmountComponentEntry(entry) {
  try {
    entry?.remove?.();
  } catch {
    /* noop */
  }
}

const normStr = (v) => (v == null || v === "" ? "" : String(v).trim().toLowerCase());

/** Wallet dismissed, shopper canceled checkout, or Adyen `Cancelled` — not a hard decline. */
function isUserCancellationLike(obj) {
  if (obj == null || typeof obj !== "object") return false;
  const rc = normStr(obj.resultCode);
  const ec = normStr(obj.errorCode);
  const name = normStr(obj.name);
  const refusal = normStr(obj.refusalReason || obj.sessionResult?.refusalReason);
  const msg = normStr(obj.message);

  if (["cancelled", "canceled", "cancel"].includes(rc)) return true;
  if (["cancel", "canceled", "cancelled"].includes(ec)) return true;
  if (name === "cancel") return true;
  if (refusal.includes("cancel")) return true;
  if (msg && msg.includes("cancel") && !msg.includes("subscription")) return true;

  const sr = obj.sessionResult;
  if (sr && typeof sr === "object") {
    const src = normStr(sr.refusalReason || sr.resultCode);
    if (src.includes("cancel") || ["cancelled", "canceled"].includes(src)) return true;
  }
  return false;
}

/**
 * Apple Pay / Google Pay often report failure with no `resultCode` when the shopper closes the sheet
 * (leads to "Payment failed with code undefined" if treated as an error).
 */
function isAmbiguousShopperAbort(data) {
  if (!data || typeof data !== "object") return false;
  const rc = data.resultCode;
  const ec = data.errorCode;
  const hasRefusal = Boolean(data.refusalReason || data.sessionResult?.refusalReason);
  return (
    (rc == null || rc === "") &&
    (ec == null || ec === "") &&
    !hasRefusal
  );
}

function isComponentValidationOnlyError(err) {
  if (!err || typeof err !== "object") return false;

  return !(
    err.resultCode ||
    err.sessionResult ||
    err.refusalReason ||
    err.pspReference ||
    err.paymentData
  );
}

const PaymentMethod = ({ amount, currency, voucherOrderData }) => {
  const { setIsTimerActive, clearTimerAfterPayment } = useBookingTimer();
  const { userID } = useCustomer();
  const { uuid, setUuid } = useBookingSession();
  const navigate = useNavigate();

  // For voucher payments, use order ID instead of booking uuid
  const isVoucherPayment = Boolean(voucherOrderData);
  const paymentUUID = isVoucherPayment ? voucherOrderData?.order?.id : uuid;
  const effectiveUserID = isVoucherPayment ? voucherOrderData?.order?.user_id : userID;

  const [complete, setComplete] = useState(false);
  const [reference, setReference] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [activeMethod, setActiveMethod] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [mountingPm, setMountingPm] = useState(false);
  const [error, setError] = useState(null);
  const [pmResponse, setPmResponse] = useState(null);
  const [failedAttemptsByMethod, setFailedAttemptsByMethod] = useState({});
  const [lockExpiries, setLockExpiries] = useState({});
  const [cardStored, setCardStored] = useState(false);
  const [selectedStoredCard, setSelectedStoredCard] = useState(null);
  const encryptedCvcRef = useRef("");
  const [cardOption, setCardOption] = useState("new");
  const [supportsApplePay, setSupportsApplePay] = useState(false);
  const [supportsGooglePay, setSupportsGooglePay] = useState(false);
  /** Bumps when user cancels a payment so express + accordion Adyen mounts refresh cleanly. */
  const [pmRemountTick, setPmRemountTick] = useState(0);

  const attemptsRef = useRef({});
  const expiriesRef = useRef({});

  const checkoutRef = useRef(null);
  const componentsRef = useRef({});
  const lastProcessedRef = useRef({});
  const scrollAnchorRef = useRef(null);

  const applePayContainerRef = useRef(null);
  const googlePayContainerRef = useRef(null);
  const paymentMountRef = useRef(null);
  const sessionInitiatedRef = useRef(false);
  const previousUuidRef = useRef(null);

  /** Latest values for Adyen session callbacks (avoid stale closures). */
  const ctxRef = useRef({
    activeMethod: null,
    cardOption: "new",
    selectedStoredCard: null,
  });

  useEffect(() => {
    ctxRef.current = { activeMethod, cardOption, selectedStoredCard };
  }, [activeMethod, cardOption, selectedStoredCard]);

  attemptsRef.current = failedAttemptsByMethod;
  expiriesRef.current = lockExpiries;

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const clientKey = process.env.REACT_APP_ADYEN_CLIENT_KEY;

  const paymentRows = useMemo(() => buildPaymentMethodRows(pmResponse), [pmResponse]);

  const resolveAttemptKeyForCallbacks = useCallback(() => {
    const { activeMethod: am, cardOption: co } = ctxRef.current;
    if (!am) {
      if (componentsRef.current[PAYMENT_METHODS.APPLEPAY]) return PAYMENT_METHODS.APPLEPAY;
      if (componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]) return PAYMENT_METHODS.GOOGLEPAY;
      return null;
    }
    if (am === PAYMENT_METHODS.CARD) return co === "new" ? "card_new" : "card_saved";
    return am;
  }, []);

  const softResetAfterUserCancel = useCallback(() => {
    setError(null);
    const optKey = resolveAttemptKeyForCallbacks();
    if (optKey) {
      setFailedAttemptsByMethod((prev) => ({
        ...prev,
        [optKey]: 0,
      }));
      setLockExpiries((prev) => ({
        ...prev,
        [optKey]: 0,
      }));
      sessionStorage.setItem(`failedAttemptsToken_${optKey}`, generateToken(optKey, 0));
    }
    setPmRemountTick((t) => t + 1);
  }, [resolveAttemptKeyForCallbacks]);

  const handleFailedAttempt = useCallback(
    (sessionResult, code, method) => {
      const errorMessages = {
        Refused: "Payment declined. Please check your card details or try another card.",
        INVALID_CARD: "Invalid card details. Please verify and try again.",
        NOT_SUPPORTED: "This payment method is not supported for this transaction.",
        ERROR: "An error occurred during payment. Please try again later.",
        CANCEL: "Payment was cancelled. Please try again or choose another method.",
        NOT_3D_SECURE: "Payment failed due to 3D Secure authentication issue.",
      };

      const isInvalidDetails =
        code?.includes("INVALID_") ||
        code === "Refused" ||
        code?.includes("ERROR_") ||
        code === "DECLINED" ||
        code === "INVALID_CARD";

    const m = method ?? resolveAttemptKeyForCallbacks();
    const co = ctxRef.current.cardOption;
    const currentKey =
      m === PAYMENT_METHODS.CARD ? (co === "new" ? "card_new" : "card_saved") : m;

      if (!isInvalidDetails || !currentKey) {
        return errorMessages[code] || `Payment failed with code ${code}. Please try again or choose another method.`;
      }

      if ((failedAttemptsByMethod[currentKey] || 0) >= MAX_ATTEMPTS) {
        const remainingTime = Math.ceil((lockExpiries[currentKey] - Date.now()) / 1000);
        return `Too many failed attempts for ${
          currentKey === "card_new"
            ? "new card"
            : currentKey === "card_saved"
              ? "saved card"
              : m
        }. Please try again after ${remainingTime} seconds or choose another method.`;
      }

      const attemptKey = `${code}-${Date.now()}`;
      if (lastProcessedRef.current[currentKey] !== attemptKey) {
        lastProcessedRef.current[currentKey] = attemptKey;
        const newAttempts = (failedAttemptsByMethod[currentKey] || 0) + 1;
        setFailedAttemptsByMethod((prev) => ({
          ...prev,
          [currentKey]: newAttempts,
        }));
        if (newAttempts >= MAX_ATTEMPTS) {
          const expiryTime = Date.now() + LOCK_DURATION_MS;
          setLockExpiries((prev) => ({
            ...prev,
            [currentKey]: expiryTime,
          }));
          return `Too many failed attempts for ${
            currentKey === "card_new"
              ? "new card"
              : currentKey === "card_saved"
                ? "saved card"
                : m
          }. Please try again after ${Math.ceil(LOCK_DURATION_MS / 1000)} seconds or choose another method.`;
        }
        const remainingAttempts = MAX_ATTEMPTS - newAttempts;
        return (
          errorMessages[code] ||
          `Invalid details entered for ${
            currentKey === "card_new"
              ? "new card"
              : currentKey === "card_saved"
                ? "saved card"
                : m
          }. Please check your details. (${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining)`
        );
      }
      const remainingAttempts = MAX_ATTEMPTS - (failedAttemptsByMethod[currentKey] || 0);
      return (
        errorMessages[code] ||
        `Invalid details entered for ${
          currentKey === "card_new"
            ? "new card"
            : currentKey === "card_saved"
              ? "saved card"
              : m
        }. Please check your details. (${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining)`
      );
    },
    [failedAttemptsByMethod, lockExpiries, resolveAttemptKeyForCallbacks]
  );

  const unmountRegularComponents = useCallback(() => {
    Object.entries(componentsRef.current).forEach(([key, component]) => {
      if (key !== PAYMENT_METHODS.APPLEPAY && key !== PAYMENT_METHODS.GOOGLEPAY) {
        unmountComponentEntry(component);
        delete componentsRef.current[key];
      }
    });
    if (paymentMountRef.current) paymentMountRef.current.innerHTML = "";
  }, []);

  useEffect(() => {
    const initialAttempts = {};
    const initialExpiries = {};
    LOCK_KEYS.forEach((key) => {
      const storedToken = sessionStorage.getItem(`failedAttemptsToken_${key}`);
      if (storedToken) {
        const { attempts, expiry } = parseToken(storedToken);
        initialAttempts[key] = attempts;
        initialExpiries[key] = expiry;
      } else {
        initialAttempts[key] = 0;
        initialExpiries[key] = 0;
      }
    });
    setFailedAttemptsByMethod(initialAttempts);
    setLockExpiries(initialExpiries);

    attemptsRef.current = initialAttempts;
    expiriesRef.current = initialExpiries;

    // Only initiate if paymentUUID exists and has changed from the previous session
    if (paymentUUID && previousUuidRef.current !== paymentUUID) {
      previousUuidRef.current = paymentUUID;
      sessionInitiatedRef.current = true;
      initiateCheckoutSession();
    }

    const interval = setInterval(() => {
      const now = Date.now();
      let hasChanges = false;
      const nextAttempts = { ...attemptsRef.current };
      const nextExpiries = { ...expiriesRef.current };

      LOCK_KEYS.forEach((key) => {
        if (nextExpiries[key] && now > nextExpiries[key]) {
          nextAttempts[key] = 0;
          nextExpiries[key] = 0;
          sessionStorage.removeItem(`failedAttemptsToken_${key}`);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        attemptsRef.current = nextAttempts;
        expiriesRef.current = nextExpiries;
        setFailedAttemptsByMethod(nextAttempts);
        setLockExpiries(nextExpiries);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      Object.values(componentsRef.current).forEach(unmountComponentEntry);
      componentsRef.current = {};
      setSelectedStoredCard(null);
      encryptedCvcRef.current = "";
      setActiveMethod(null);
      setSelectedRowKey(null);
      setCheckoutReady(false);
    };
    // Session bootstrap intentionally excludes unstable initiateCheckoutSession identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- booking/voucher bootstrap only
  }, [paymentUUID, effectiveUserID]);

  useEffect(() => {
    LOCK_KEYS.forEach((key) => {
      const attempts = failedAttemptsByMethod[key];
      if (attempts > 0) {
        sessionStorage.setItem(`failedAttemptsToken_${key}`, generateToken(key, attempts));
      } else {
        sessionStorage.removeItem(`failedAttemptsToken_${key}`);
      }
    });
  }, [failedAttemptsByMethod]);

  const initiateCheckoutSession = async () => {
    if (!baseUrl || !clientKey) {
      setError("There is a problem with the payment setup.");
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
      return;
    }

    if (!effectiveUserID) {
      setError("User ID is missing. Please log in and try again.");
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
      return;
    }

    let adjustedShopperReference = effectiveUserID.toString();
    if (adjustedShopperReference.length < 3) {
      adjustedShopperReference = adjustedShopperReference.padStart(3, "0");
    }

    setLoading(true);
    setCheckoutReady(false);
    setError(null);
    setComplete(false);
    setCardStored(false);
    setSupportsApplePay(false);
    setSupportsGooglePay(false);

    try {
      let payload;

      if (isVoucherPayment) {
        // Voucher payment payload
        payload = {
          type: "voucher",
          uuid: paymentUUID,
          amount: {
            value: amount * 100,
            currency,
          },
          panel: "customer",
        };
      } else {
        // Booking payment payload
        payload = {
          customerID: adjustedShopperReference,
          uuid: paymentUUID,
          amount: { value: amount * 100, currency },
          shopperReference: adjustedShopperReference,
          panel: "customer",
        };
      }

      const { activeMethod: am, selectedStoredCard: sc } = ctxRef.current;
      if (am === PAYMENT_METHODS.CARD && !sc) {
        payload.recurringProcessingModel = "CardOnFile";
      }

      logger.info('Payment session initiated', { uuid: payload.uuid, panel: payload.panel });

      // No React Query retry here — payment initiate must not auto-retry (duplicate charges).
      const { data } = await axios.post(`${baseUrl}/payment/initiate/oneclick`, payload, {
        timeout: 30000,
      });
      setReference(data.reference);

      const checkout = await AdyenCheckout({
        environment: "test",
        clientKey,
        session: { id: data.id, sessionData: data.sessionData },
        amount,
        locale: "en-US",
        countryCode: "SG",
        onPaymentCompleted: async (result) => {
          if (result.resultCode === "Authorised") {
            setComplete(true);
            LOCK_KEYS.forEach((key) => sessionStorage.removeItem(`failedAttemptsToken_${key}`));
            setFailedAttemptsByMethod({});
            setLockExpiries({});
            setIsTimerActive(false);
            clearTimerAfterPayment();
            if (!isVoucherPayment) setUuid(null);

            const { activeMethod: amCb, selectedStoredCard: scCb } = ctxRef.current;
            if (
              amCb === PAYMENT_METHODS.CARD &&
              !scCb &&
              result.additionalData?.storedPaymentMethodId
            ) {
              setCardStored(true);
            }

            if (isVoucherPayment) {
              const orderId = voucherOrderData?.order?.id || voucherOrderData?.id;
              navigate(`/voucher/${orderId}`);
            } else {
              navigateToBookedAppointment(navigate, uuid);
            }
          } else {
            const expressFallback =
              componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
                ? PAYMENT_METHODS.APPLEPAY
                : PAYMENT_METHODS.GOOGLEPAY;
            if (isUserCancellationLike(result)) {
              softResetAfterUserCancel();
            } else {
              const errMsg = handleFailedAttempt(
                result.sessionResult,
                result.resultCode,
                ctxRef.current.activeMethod || expressFallback
              );
              setError(errMsg);
              if (errMsg.toLowerCase().includes("too many")) setSessionExpired(true);
            }
          }
        },
        onAdditionalDetails: async (state, component) => {
          try {
            const amt = component.props?.amount || { value: 0, currency: "Unknown" };
            // No auto-retry — payment callback must remain single-flight
            const response = await axios.post(
              `${baseUrl}/payment/callback`,
              {
                details: state.data.details,
                paymentData: state.data.paymentData,
                amount: {
                  value: amt.value,
                  currency: amt.currency,
                },
              },
              { timeout: 30000 }
            );

            const result = response.data.data.data;

            if (result.resultCode === "Authorised") {
              setComplete(true);
              clearTimerAfterPayment();
              if (!isVoucherPayment) setUuid(null);
              if (isVoucherPayment) {
                const orderId = voucherOrderData?.order?.id || voucherOrderData?.id;
                navigate(`/voucher/${orderId}`);
              } else {
                navigateToBookedAppointment(navigate, uuid);
              }
            } else if (isUserCancellationLike(result) || isAmbiguousShopperAbort(result)) {
              softResetAfterUserCancel();
            } else {
              setError(handleFailedAttempt(result.sessionResult, result.resultCode));
            }
          } catch (err) {
            logger.error("Error processing additional details:", err);
            setError("Failed to process QR payment details.");
          }
        },
        onPaymentFailed: (data, component) => {
          const expressFallback =
            componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
              ? PAYMENT_METHODS.APPLEPAY
              : PAYMENT_METHODS.GOOGLEPAY;
          if (isAdyenSessionExpiredError(data)) {
            setSessionExpired(true);
            setError("Too many failed payment attempts. Your session has expired. Please return to billing details to try again.");
            return;
          }
          if (isUserCancellationLike(data) || isAmbiguousShopperAbort(data)) {
            softResetAfterUserCancel();
          } else {
            const errMsg = handleFailedAttempt(
              data.sessionResult,
              data.resultCode || data.errorCode,
              ctxRef.current.activeMethod || expressFallback
            );
            setError(errMsg);
            if (errMsg.toLowerCase().includes("too many")) setSessionExpired(true);
            if (component?.handleError) component.handleError(data);
          }
        },
        onError: (err) => {
          // Temporary: log the raw Adyen error so the exact shape can be confirmed in DevTools.
          logger.error("[Adyen onError]", JSON.stringify(err, null, 2), err);
          const expressFallback =
            componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
              ? PAYMENT_METHODS.APPLEPAY
              : PAYMENT_METHODS.GOOGLEPAY;
          const errCode = err.errorCode || err.name;

          if (
            errCode === "CANCEL" ||
            errCode === "CANCELED" ||
            isUserCancellationLike(err) ||
            isUserCancellationLike({ ...err, errorCode: errCode })
          ) {
            softResetAfterUserCancel();
            return;
          }

          if (isAdyenSessionExpiredError(err)) {
            setSessionExpired(true);
            setError("Too many failed payment attempts. Your session has expired. Please return to billing details to try again.");
            return;
          }

          const methodForErr = ctxRef.current.activeMethod || expressFallback;
          if (
            methodForErr === PAYMENT_METHODS.CARD &&
            isComponentValidationOnlyError(err)
          ) {
            encryptedCvcRef.current = "";
            return;
          }

          const errorMessage = handleFailedAttempt(
            err.sessionResult,
            err.resultCode || err.errorCode,
            methodForErr
          );
          setError(errorMessage);
          if (errorMessage.toLowerCase().includes("too many")) setSessionExpired(true);
        },
      });

      checkoutRef.current = checkout;
      const pmRes = checkout.paymentMethodsResponse || null;
      setPmResponse(pmRes);

      /**
       * Apple Pay requires a secure document (`https:`). Plain `http://localhost` throws
       * InvalidAccessError when starting a session — only enable Apple Pay on HTTPS (e.g. https://localhost with a dev cert).
       * Google Pay TEST often works on http localhost; keep that for dev.
       */
      const pmList = pmRes?.paymentMethods ?? [];
      const hasApple = pmList.some((pm) => pm.type === "applepay");
      const hasGoogle = pmList.some((pm) => pm.type === "googlepay" || pm.type === "paywithgoogle");

      const isHttps =
        typeof window !== "undefined" && window.location.protocol === "https:";
      const googleOkOnHttpLocal =
        typeof window !== "undefined" &&
        window.location.protocol === "http:" &&
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

      setSupportsApplePay(isHttps && hasApple);
      setSupportsGooglePay((isHttps || googleOkOnHttpLocal) && hasGoogle);

      setLoading(false);
      setCheckoutReady(true);
    } catch (err) {
      logger.error("Session did not start: initiateCheckoutSession", err);
      if (isAdyenSessionExpiredError(err)) {
        setSessionExpired(true);
        setError("Your payment session has expired. Please start a new session or contact us if this keeps happening.");
      } else {
        setError(err.response?.data?.message || err.message || "Failed to start payment session. Please try again.");
      }
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
    }
  };

  /** Express checkout: Apple Pay & Google Pay — mounted once per session when supported. */
  useLayoutEffect(() => {
    if (!checkoutReady || loading || !checkoutRef.current) return;

    const mountExpress = () => {
      if (supportsApplePay && applePayContainerRef.current) {
        try {
          unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.APPLEPAY]);
          delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
          applePayContainerRef.current.innerHTML = "";
          const cmp = createApplePayComponent(checkoutRef.current, { amount, currency });
          cmp.mount(applePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.APPLEPAY] = cmp;
        } catch (err) {
          logger.error("ApplePay initialization failed:", err);
          setError("Apple Pay is not available. Please try another method.");
        }
      } else {
        unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.APPLEPAY]);
        delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
        if (applePayContainerRef.current) applePayContainerRef.current.innerHTML = "";
      }

      if (supportsGooglePay && googlePayContainerRef.current) {
        try {
          unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]);
          delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
          googlePayContainerRef.current.innerHTML = "";
          const cmp = createGooglePayComponent(checkoutRef.current);
          cmp.mount(googlePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.GOOGLEPAY] = cmp;
        } catch (err) {
          logger.error("GooglePay initialization failed:", err);
          setError("Google Pay is not available. Please try another method.");
        }
      } else {
        unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]);
        delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
        if (googlePayContainerRef.current) googlePayContainerRef.current.innerHTML = "";
      }
    };

    requestAnimationFrame(mountExpress);
  }, [checkoutReady, loading, supportsApplePay, supportsGooglePay, amount, currency, pmRemountTick]);

  /** Regular accordion methods — single mount target. */
  useLayoutEffect(() => {
    if (!checkoutReady || loading || !checkoutRef.current || !selectedRowKey || !activeMethod) {
      return undefined;
    }

    if (activeMethod === PAYMENT_METHODS.APPLEPAY || activeMethod === PAYMENT_METHODS.GOOGLEPAY) {
      return undefined;
    }

    const row = paymentRows.find((r) => r.rowKey === selectedRowKey);
    if (!row || row.adyenType === "applepay" || row.adyenType === "paywithgoogle") {
      return undefined;
    }

    let cancelled = false;
    setMountingPm(true);

    const runMount = () => {
      if (cancelled || !checkoutRef.current || !paymentMountRef.current) {
        setMountingPm(false);
        return;
      }

      unmountRegularComponents();

      const onCardChange = (state) => {
        if (state.isValid && state.data?.paymentMethod?.encryptedSecurityCode) {
          encryptedCvcRef.current = state.data.paymentMethod.encryptedSecurityCode;
        } else {
          encryptedCvcRef.current = "";
        }
      };

      const onCardError = () => {
        encryptedCvcRef.current = "";
      };

      const ctx = {
        amount,
        currency,
        baseUrl,
        selectedStoredCard,
        onCardChange,
        onCardError,
      };

      const component = createPaymentComponent(checkoutRef.current, row, ctx);
      if (cancelled || !component) {
        setMountingPm(false);
        if (!component) setError(`Payment method ${row.label} could not be loaded.`);
        return;
      }

      try {
        component.mount(paymentMountRef.current);
        const refKey = row.internalKey;
        componentsRef.current[refKey] = component;
        setMountingPm(false);
      } catch (e) {
        logger.error(e);
        setMountingPm(false);
        setError(`Payment method ${row.label} not loaded: ${e.message}`);
      }
    };

    requestAnimationFrame(runMount);

    return () => {
      cancelled = true;
      setMountingPm(false);
    };
  }, [
    checkoutReady,
    loading,
    selectedRowKey,
    activeMethod,
    paymentRows,
    cardOption,
    selectedStoredCard,
    amount,
    currency,
    baseUrl,
    unmountRegularComponents,
    pmRemountTick,
  ]);

  const handleRetry = async () => {
    setActiveMethod(null);
    setSelectedRowKey(null);
    setSelectedStoredCard(null);
    encryptedCvcRef.current = "";
    setError(null);
    setSessionExpired(false);
    setComplete(false);
    setCardStored(false);
    setLoading(false);
    setReference(null);
    setCardOption("new");

    // New Adyen session = fresh attempt history
    LOCK_KEYS.forEach((key) => sessionStorage.removeItem(`failedAttemptsToken_${key}`));
    setFailedAttemptsByMethod({});
    setLockExpiries({});

    Object.entries(componentsRef.current).forEach(([, component]) => unmountComponentEntry(component));
    componentsRef.current = {};

    checkoutRef.current = null;
    setPmResponse(null);
    setCheckoutReady(false);

    await initiateCheckoutSession();
  };

  const handleSwitchMethod = async () => {
    setError(null);
    setLoading(false);
    setComplete(false);
    setCardStored(false);
    encryptedCvcRef.current = "";
    setActiveMethod(null);
    setSelectedRowKey(null);
    setSelectedStoredCard(null);
    setCardOption("new");

    Object.entries(componentsRef.current).forEach(([, component]) => unmountComponentEntry(component));
    componentsRef.current = {};

    if (paymentMountRef.current) paymentMountRef.current.innerHTML = "";

    if (checkoutRef.current && (supportsApplePay || supportsGooglePay)) {
      delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
      delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
      await new Promise((r) => requestAnimationFrame(r));
      try {
        if (supportsApplePay && applePayContainerRef.current) {
          applePayContainerRef.current.innerHTML = "";
          const cmp = createApplePayComponent(checkoutRef.current, { amount, currency });
          cmp.mount(applePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.APPLEPAY] = cmp;
        }
        if (supportsGooglePay && googlePayContainerRef.current) {
          googlePayContainerRef.current.innerHTML = "";
          const cmp = createGooglePayComponent(checkoutRef.current);
          cmp.mount(googlePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.GOOGLEPAY] = cmp;
        }
      } catch (e) {
        logger.error(e);
      }
    }
  };

  const handleSelectRow = (row) => {
    if (scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    if (selectedRowKey === row.rowKey) {
      setSelectedRowKey(null);
      setActiveMethod(null);
      encryptedCvcRef.current = "";
      unmountRegularComponents();
      return;
    }

    const blocked =
      (failedAttemptsByMethod[row.internalKey] || 0) >= MAX_ATTEMPTS &&
      Date.now() < (lockExpiries[row.internalKey] || 0);
    if (blocked) return;

    setSelectedRowKey(row.rowKey);
    setActiveMethod(row.internalKey);
    setSelectedStoredCard(null);
    encryptedCvcRef.current = "";
    setCardOption("new");
  };

  const isGooglePayLocked =
    (failedAttemptsByMethod[PAYMENT_METHODS.GOOGLEPAY] || 0) >= MAX_ATTEMPTS &&
    Date.now() < (lockExpiries[PAYMENT_METHODS.GOOGLEPAY] || 0);
  const isApplePayLocked =
    (failedAttemptsByMethod[PAYMENT_METHODS.APPLEPAY] || 0) >= MAX_ATTEMPTS &&
    Date.now() < (lockExpiries[PAYMENT_METHODS.APPLEPAY] || 0);

  const isMethodLocked = (internalKey) =>
    (failedAttemptsByMethod[internalKey] || 0) >= MAX_ATTEMPTS &&
    Date.now() < (lockExpiries[internalKey] || 0);

  const lockSecondsRemaining = (internalKey) =>
    Math.max(0, Math.ceil(((lockExpiries[internalKey] || 0) - Date.now()) / 1000));

  const renderExpanded = () => (
    <PaymentMethodRenderer mounting={mountingPm} mountRef={paymentMountRef} />
  );

  return (
    <div className="payment-method" ref={scrollAnchorRef}>
      <PaymentStatus
        complete={complete}
        reference={reference}
        cardStored={cardStored}
        loading={loading}
        error={error}
        sessionExpired={sessionExpired}
        activeMethod={activeMethod}
        failedAttemptsByMethod={failedAttemptsByMethod}
        lockExpiries={lockExpiries}
        initiateCheckoutSession={handleRetry}
        handleNewSession={() => navigate('/billing-details')}
        handleSwitchMethod={handleSwitchMethod}
        selectedStoredCard={selectedStoredCard}
      />
      {!complete && !loading && !error && (
        <>
          <ExpressCheckout
            supportsApplePay={supportsApplePay}
            supportsGooglePay={supportsGooglePay}
            isApplePayLocked={isApplePayLocked}
            isGooglePayLocked={isGooglePayLocked}
            appleLockSeconds={lockSecondsRemaining(PAYMENT_METHODS.APPLEPAY)}
            googleLockSeconds={lockSecondsRemaining(PAYMENT_METHODS.GOOGLEPAY)}
            applePayRef={applePayContainerRef}
            googlePayRef={googlePayContainerRef}
          />

          {(supportsApplePay || supportsGooglePay) && (
            <h2 className={`${paymentStyles.sectionTitle} titleSub`}>More payment options</h2>
          )}

          {!supportsApplePay && !supportsGooglePay ? (
            <h2 className={`${paymentStyles.sectionTitle} titleSub`}>Payment methods</h2>
          ) : null}

          <PaymentAccordion
            rows={paymentRows}
            selectedRowKey={selectedRowKey}
            onSelectRow={handleSelectRow}
            isMethodLocked={isMethodLocked}
            lockSecondsRemaining={lockSecondsRemaining}
            renderExpanded={renderExpanded}
          />
        </>
      )}
    </div>
  );
};

// Wrap with Error Boundary to catch payment errors
const PaymentMethodWithErrorBoundary = (props) => (
  <ErrorBoundary componentName="Payment Method" fallbackMessage="Payment processing encountered an error. Please try again or use a different payment method.">
    <PaymentMethod {...props} />
  </ErrorBoundary>
);

export default PaymentMethodWithErrorBoundary;
